import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
    ArrowLeft, Coins, Lock, Loader2, Skull, Trophy, Heart,
    Map as MapIcon, ShieldCheck, Swords, Users, Wallet,
} from "lucide-react";
import VoxelScene from "../components/WastesScene3D";
import MmoShopModal from "../components/MmoShopModal";

const VIEW_W = 1000;
const VIEW_H = 620;

function wsUrl(token) {
    return `${API.replace(/^http/, "ws")}/mmo/ws?token=${encodeURIComponent(token)}`;
}

function Hud({ you, online, leaderboard, onBank, onShop }) {
    if (!you) return null;
    const hpPct = Math.max(0, Math.min(100, (you.hp / you.max_hp) * 100));
    return (
        <>
            {/* top-left HUD */}
            <div className="absolute top-2 left-2 space-y-1 pointer-events-none">
                <div data-testid="mmo-hud-hp" className="bg-white border-2 border-ink rounded-lg px-1.5 py-1 shadow-brutal w-28">
                    <div className="flex items-center justify-between mb-0.5">
                        <span className="font-heading font-bold text-[8px] uppercase tracking-wider text-ink/60">HP</span>
                        <span className="font-heading font-black text-[10px] tabular-nums text-ink">{Math.round(you.hp)}/{you.max_hp}</span>
                    </div>
                    <div className="h-1.5 bg-ink/10 rounded-full overflow-hidden border border-ink">
                        <div className={hpPct < 30 ? "h-full bg-vibrantpink" : "h-full bg-vibrantorange"} style={{ width: `${hpPct}%` }} />
                    </div>
                </div>
                <div data-testid="mmo-hud-carry" className="bg-vibrantyellow border-2 border-ink rounded-lg px-1.5 py-1 shadow-brutal w-28 flex items-center justify-between">
                    <div className="flex items-center gap-1">
                        <Coins className="w-2.5 h-2.5 text-ink" strokeWidth={2.5} />
                        <span className="font-heading font-bold text-[8px] uppercase tracking-wider text-ink/60">Carry</span>
                    </div>
                    <span className="font-heading font-black text-[11px] text-ink tabular-nums">{you.carry}</span>
                </div>
                <div className="grid grid-cols-2 gap-1 w-28">
                    <div className="bg-white border-2 border-ink rounded-lg px-1 py-0.5 shadow-brutal flex items-center justify-between">
                        <Swords className="w-2.5 h-2.5 text-ink" strokeWidth={2.5} />
                        <span className="font-heading font-black text-[10px] text-ink tabular-nums" data-testid="mmo-hud-kills">{you.kills}</span>
                    </div>
                    <div className="bg-white border-2 border-ink rounded-lg px-1 py-0.5 shadow-brutal flex items-center justify-between">
                        <Skull className="w-2.5 h-2.5 text-ink" strokeWidth={2.5} />
                        <span className="font-heading font-black text-[10px] text-ink tabular-nums" data-testid="mmo-hud-deaths">{you.deaths}</span>
                    </div>
                </div>
                <div data-testid="mmo-hud-level" className="bg-emerald-600 border-2 border-ink rounded-lg px-1.5 py-1 shadow-brutal w-28">
                    <div className="flex items-center justify-between mb-0.5">
                        <span className="font-heading font-bold text-[8px] uppercase tracking-wider text-white/80">Cmb lvl</span>
                        <span className="font-heading font-black text-[10px] text-white tabular-nums">{you.combat_level ?? 0}</span>
                    </div>
                    {(() => {
                        const xp = you.combat_xp || 0;
                        const floor = you.xp_curr_floor || 0;
                        const next = you.xp_next || 5;
                        const progress = Math.max(0, Math.min(100, ((xp - floor) / Math.max(1, next - floor)) * 100));
                        return (
                            <>
                                <div className="h-1 bg-black/30 rounded-full overflow-hidden border border-ink">
                                    <div className="h-full bg-vibrantyellow" style={{ width: `${progress}%` }} />
                                </div>
                                <div className="font-body text-[7px] text-white/80 mt-0.5 text-right tabular-nums">{xp}/{next}</div>
                            </>
                        );
                    })()}
                </div>
                {/* Daily mob credit cap meter */}
                {(() => {
                    const cap = you.daily_mob_cap ?? 5;
                    const earned = you.daily_mob_credits ?? 0;
                    const pct = Math.max(0, Math.min(100, (earned / cap) * 100));
                    const maxed = earned >= cap;
                    return (
                        <div data-testid="mmo-hud-daily" className={`border-2 border-ink rounded-lg px-1.5 py-1 shadow-brutal w-28 ${maxed ? "bg-ink text-white" : "bg-white"}`}>
                            <div className="flex items-center justify-between mb-0.5">
                                <span className={`font-heading font-bold text-[8px] uppercase tracking-wider ${maxed ? "text-white/70" : "text-ink/60"}`}>Daily mob</span>
                                <span className={`font-heading font-black text-[10px] tabular-nums ${maxed ? "text-white" : "text-ink"}`}>{earned.toFixed(1)}/{cap}</span>
                            </div>
                            <div className={`h-1 rounded-full overflow-hidden border border-ink ${maxed ? "bg-white/20" : "bg-ink/10"}`}>
                                <div className={`h-full ${maxed ? "bg-vibrantpink" : "bg-vibrantorange"}`} style={{ width: `${pct}%` }} />
                            </div>
                        </div>
                    );
                })()}
                {you.safe && (
                    <div data-testid="mmo-hud-safe" className="bg-vibrantblue border-2 border-ink rounded-lg px-1.5 py-0.5 shadow-brutal w-28 flex items-center gap-1">
                        <ShieldCheck className="w-2.5 h-2.5 text-white" strokeWidth={2.5} />
                        <span className="font-heading font-bold text-[8px] uppercase tracking-wider text-white">Safe Town</span>
                    </div>
                )}
            </div>

            {/* top-right */}
            <div className="absolute top-2 right-2 space-y-1 w-28">
                <div className="bg-white border-2 border-ink rounded-lg px-1.5 py-1 shadow-brutal flex items-center justify-between">
                    <div className="flex items-center gap-1">
                        <Users className="w-2.5 h-2.5 text-ink" strokeWidth={2.5} />
                        <span className="font-heading font-bold text-[8px] uppercase tracking-wider text-ink/60">Online</span>
                    </div>
                    <span className="font-heading font-black text-[11px] text-ink tabular-nums" data-testid="mmo-online">{online}</span>
                </div>
                <div data-testid="mmo-leaderboard" className="bg-white border-2 border-ink rounded-lg p-1.5 shadow-brutal">
                    <div className="flex items-center gap-1 mb-1">
                        <Trophy className="w-2.5 h-2.5 text-vibrantorange" strokeWidth={2.5} />
                        <span className="font-heading font-black text-[8px] uppercase tracking-wider text-ink">Top carry</span>
                    </div>
                    {leaderboard.length === 0 ? (
                        <div className="font-body text-[8px] text-ink/40">No players</div>
                    ) : leaderboard.map((p, i) => (
                        <div key={i} className="flex items-center justify-between font-heading text-[9px] py-0">
                            <span className="font-bold text-ink/80 truncate">{i + 1}. {p.name}</span>
                            <span className="font-black text-ink tabular-nums">{p.carry}</span>
                        </div>
                    ))}
                </div>
                {you.safe && (
                    <>
                        <button
                            onClick={onBank}
                            data-testid="mmo-deposit-button"
                            className="brutal-btn-primary w-full text-[10px] py-1 px-1.5"
                        >
                            <Wallet className="w-2.5 h-2.5 mr-1" strokeWidth={2.5} /> Deposit
                        </button>
                        <button
                            onClick={onShop}
                            data-testid="mmo-shop-button"
                            className="brutal-btn w-full text-[10px] py-1 px-1.5 bg-ink text-white"
                        >
                            <ShieldCheck className="w-2.5 h-2.5 mr-1" strokeWidth={2.5} /> Armoury
                        </button>
                    </>
                )}
            </div>
        </>
    );
}

function Minimap({ you, players, world }) {
    if (!you || !world) return null;
    const w = 130, h = 130;
    const sx = w / world.w, sy = h / world.h;
    const safe = world.safe;
    return (
        <div data-testid="mmo-minimap" className="absolute bottom-3 right-3 bg-white border-[3px] border-ink shadow-brutal rounded-xl p-1.5">
            <svg width={w} height={h} className="rounded-md" style={{ background: "#163C2A" }}>
                <rect x={safe[0] * sx} y={safe[1] * sy} width={(safe[2] - safe[0]) * sx} height={(safe[3] - safe[1]) * sy} fill="#FBBF24" opacity="0.4" />
                {players.map((p) => (
                    <circle key={p.uid} cx={p.x * sx} cy={p.y * sy} r={1.5} fill="#3B82F6" />
                ))}
                <circle cx={you.x * sx} cy={you.y * sy} r={2.5} fill="#FB923C" stroke="#0A0A0A" strokeWidth={0.6} />
            </svg>
        </div>
    );
}

export default function Wastes() {
    const { user, loading: authLoading, refresh } = useAuth();
    const canvasRef = useRef(null);
    const wsRef = useRef(null);
    const stateRef = useRef(null);
    const keysRef = useRef({ w: false, a: false, s: false, d: false });
    const aimRef = useRef({ x: 0, y: 0 });
    const cameraYawRef = useRef(0);
    const firingRef = useRef(false);
    const lastInputSentRef = useRef(0);
    const rafRef = useRef(0);
    // Click-to-move target in world coords ({x, y} or null)
    const moveTargetRef = useRef(null);
    // Picker function exposed by the 3D scene: canvasX,Y -> world {x, y} | null
    const pickerRef = useRef(null);
    // Entity picker: canvasX,Y -> {type, id} | null
    const entityPickerRef = useRef(null);
    // Auto-attack target {type: "player"|"mob", id} or null
    const attackTargetRef = useRef(null);

    const [, forceRender] = useState(0);
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState("");
    const [credits, setCredits] = useState(0);
    const [depositOpen, setDepositOpen] = useState(false);
    const [shopOpen, setShopOpen] = useState(false);
    const [depositAmt, setDepositAmt] = useState(20);
    const [depositErr, setDepositErr] = useState("");
    const [depositing, setDepositing] = useState(false);
    const [bankedToast, setBankedToast] = useState(0);
    // RuneScape-style chat
    const [chatMessages, setChatMessages] = useState([]);  // [{eid, name, text, uid}]
    const [chatInput, setChatInput] = useState("");
    const seenChatEids = useRef(new Set());
    const chatScrollRef = useRef(null);

    const isLoggedIn = user && user !== false;

    const loadBalance = useCallback(async () => {
        if (!isLoggedIn) return;
        try {
            const { data } = await api.get("/mmo/character");
            setCredits(data.credits);
        } catch { /* ignore */ }
    }, [isLoggedIn]);

    useEffect(() => { loadBalance(); }, [loadBalance]);

    useEffect(() => {
        if (chatScrollRef.current) {
            chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        }
    }, [chatMessages.length]);

    const sendChat = (text) => {
        const ws = wsRef.current;
        const clean = String(text || "").trim();
        if (!ws || ws.readyState !== 1 || !clean) return;
        ws.send(JSON.stringify({ type: "chat", text: clean }));
        setChatInput("");
    };

    // ---------- Connect ----------
    const connect = useCallback(() => {
        const token = localStorage.getItem("vp_token");
        if (!token) { setError("Please log in again"); return; }
        const ws = new WebSocket(wsUrl(token));
        wsRef.current = ws;
        ws.onopen = () => setConnected(true);
        ws.onmessage = (ev) => {
            const m = JSON.parse(ev.data);
            if (m.type === "state") {
                stateRef.current = m.state;
                if (m.state?.you?.just_banked) {
                    setBankedToast(m.state.you.just_banked);
                    loadBalance();
                    setTimeout(() => setBankedToast(0), 2500);
                }
                // Pull any new chat messages out of the realm-wide log
                if (m.state?.chats && m.state.chats.length > 0) {
                    const fresh = m.state.chats.filter((c) => !seenChatEids.current.has(c.eid));
                    if (fresh.length > 0) {
                        fresh.forEach((c) => seenChatEids.current.add(c.eid));
                        setChatMessages((prev) => {
                            const next = [...prev, ...fresh];
                            return next.length > 60 ? next.slice(-60) : next;
                        });
                    }
                }
            } else if (m.type === "welcome") {
                setCredits(m.balance);
            }
        };
        ws.onclose = () => { setConnected(false); wsRef.current = null; };
        ws.onerror = () => setError("Connection error");
    }, [loadBalance]);

    useEffect(() => {
        if (!isLoggedIn) return;
        connect();
        return () => {
            if (wsRef.current) try { wsRef.current.close(); } catch { /* ignore */ }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    // ---------- Input handling ----------
    // Camera yaw velocity. A = rotate left, D = rotate right.
    const yawVelRef = useRef(0);
    useEffect(() => {
        const ROT_SPEED = 2.2;   // radians per second when holding A or D
        const down = (e) => {
            const k = e.key.toLowerCase();
            if (k === "a") {
                yawVelRef.current = -ROT_SPEED;
                e.preventDefault();
            } else if (k === "d") {
                yawVelRef.current = ROT_SPEED;
                e.preventDefault();
            } else if (k === "r") {
                const ws = wsRef.current;
                if (ws && ws.readyState === 1) ws.send(JSON.stringify({ type: "respawn" }));
            } else if (k === "escape") {
                // Cancel any active target
                moveTargetRef.current = null;
                attackTargetRef.current = null;
            }
        };
        const up = (e) => {
            const k = e.key.toLowerCase();
            if (k === "a" && yawVelRef.current < 0) yawVelRef.current = 0;
            if (k === "d" && yawVelRef.current > 0) yawVelRef.current = 0;
        };
        window.addEventListener("keydown", down);
        window.addEventListener("keyup", up);
        return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
    }, []);

    // Camera rotation tick — runs ~60Hz, integrates A/D into cameraYawRef
    useEffect(() => {
        let raf = 0;
        let last = performance.now();
        const loop = () => {
            const now = performance.now();
            const dt = Math.min(0.1, (now - last) / 1000);
            last = now;
            if (yawVelRef.current !== 0) {
                cameraYawRef.current = (cameraYawRef.current || 0) + yawVelRef.current * dt;
            }
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, []);

    // ---------- Send input frames at 20Hz ----------
    useEffect(() => {
        const t = setInterval(() => {
            const ws = wsRef.current;
            const st = stateRef.current;
            if (!ws || ws.readyState !== 1 || !st?.you) return;

            const yaw = cameraYawRef.current || 0;
            let serverKeys;
            const tgt = moveTargetRef.current;
            const atk = attackTargetRef.current;

            if (atk) {
                // Auto-attack: server walks us into range and swings. Don't send keys.
                serverKeys = { w: false, a: false, s: false, d: false };
            } else if (tgt) {
                // Click-to-move: head straight toward the world target
                const dx = tgt.x - st.you.x;
                const dy = tgt.y - st.you.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist < 22) {
                    moveTargetRef.current = null;
                    serverKeys = { w: false, a: false, s: false, d: false };
                } else {
                    const nx = dx / dist;
                    const ny = dy / dist;
                    serverKeys = {
                        w: ny < -0.3,
                        s: ny > 0.3,
                        a: nx < -0.3,
                        d: nx > 0.3,
                    };
                }
            } else {
                // Idle — no movement
                serverKeys = { w: false, a: false, s: false, d: false };
            }
            // Aim still tracks the current camera yaw (for any future ranged use).
            const sinY = Math.sin(yaw), cosY = Math.cos(yaw);
            const aimX = st.you.x + sinY * 500;
            const aimY = st.you.y + cosY * 500;
            ws.send(JSON.stringify({
                type: "input",
                frame: {
                    keys: serverKeys,
                    aim: [aimX, aimY],
                    target: atk ? { type: atk.type, id: atk.id } : null,
                },
            }));
            lastInputSentRef.current = performance.now();
        }, 50);
        return () => clearInterval(t);
    }, []);

    // Tick a 5Hz re-render so the HUD (which reads stateRef inside React) updates
    useEffect(() => {
        const t = setInterval(() => forceRender((n) => n + 1), 200);
        return () => clearInterval(t);
    }, []);

    // Canvas mouse handlers
    const onMouseMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        aimRef.current = {
            x: ((e.clientX - r.left) / r.width) * VIEW_W,
            y: ((e.clientY - r.top) / r.height) * VIEW_H,
        };
    };
    const onMouseDown = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const cx = e.clientX - r.left;
        const cy = e.clientY - r.top;
        if (e.button === 2) {
            // Right-click = move to that ground point (RuneScape-style)
            e.preventDefault();
            const picker = pickerRef.current;
            if (!picker) return;
            const world = picker(cx, cy);
            if (world) {
                moveTargetRef.current = world;
                attackTargetRef.current = null;
            }
            return;
        }
        if (e.button === 0) {
            // Left-click = attack the entity under cursor (mob or other player)
            const ePicker = entityPickerRef.current;
            const ent = ePicker ? ePicker(cx, cy) : null;
            if (ent && ent.id) {
                // Ignore self-clicks
                const myUid = stateRef.current?.you?.uid;
                if (ent.type === "player" && ent.id === myUid) return;
                attackTargetRef.current = { type: ent.type, id: ent.id };
                moveTargetRef.current = null;
            }
            // Click on empty ground does nothing — use right-click to move
        }
    };
    const onMouseUp = () => { /* no-op in melee mode */ };

    const doDeposit = async () => {
        setDepositErr("");
        setDepositing(true);
        try {
            const { data } = await api.post("/mmo/deposit", { amount: parseInt(depositAmt, 10) || 0 });
            setCredits(data.credits);
            setDepositOpen(false);
            if (refresh) refresh();
        } catch (e) {
            setDepositErr(e?.response?.data?.detail || "Couldn't deposit");
        } finally {
            setDepositing(false);
        }
    };

    if (authLoading) return <div className="p-10 font-heading">Loading…</div>;
    if (!isLoggedIn) {
        return (
            <div className="max-w-2xl mx-auto p-8">
                <div data-testid="mmo-login-gate" className="brutal-card p-8 bg-white">
                    <div className="w-12 h-12 bg-vibrantpink border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal mb-4">
                        <Lock className="w-6 h-6 text-white" strokeWidth={2.5} />
                    </div>
                    <h2 className="font-heading font-black text-3xl text-ink tracking-tighter mb-2">Log in to enter the Wastes</h2>
                    <p className="font-body text-ink/70 mb-5">Open PvP. Real credit stakes. Bank often.</p>
                    <div className="flex gap-3">
                        <Link to="/login" className="brutal-btn-primary">Log in</Link>
                        <Link to="/register" className="brutal-btn-secondary">Create account</Link>
                    </div>
                </div>
            </div>
        );
    }

    const state = stateRef.current;
    const you = state?.you;

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <Link to="/" data-testid="mmo-back" className="inline-flex items-center gap-1.5 font-heading font-bold text-xs uppercase tracking-widest text-ink/60 hover:text-vibrantorange transition-colors mb-3">
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
                Back to Endfb
            </Link>
            <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-vibrantorange border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal-lg">
                    <MapIcon className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                    <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-ink leading-tight">
                        Endfb <span className="text-vibrantpink">Wastes</span>
                    </h1>
                    <p className="font-body text-ink/70 text-sm">
                        Bring credits to the field. Kill players & mobs to loot. Bank in Safe Town to lock in winnings.
                    </p>
                </div>
                <div className="hidden sm:flex items-center gap-1.5 bg-vibrantyellow border-[3px] border-ink rounded-xl px-3 py-2 shadow-brutal">
                    <Coins className="w-4 h-4 text-ink" strokeWidth={2.5} />
                    <span className="font-heading font-bold text-sm text-ink" data-testid="mmo-credits">{credits}</span>
                </div>
            </div>

            <div className="relative" style={{ width: VIEW_W, maxWidth: "100%", margin: "0 auto" }}>
                <div
                    onMouseMove={onMouseMove}
                    onMouseDown={onMouseDown}
                    onMouseUp={onMouseUp}
                    onMouseLeave={() => { firingRef.current = false; }}
                    onContextMenu={(e) => e.preventDefault()}
                    data-testid="mmo-canvas"
                    className="border-[3px] border-ink rounded-2xl shadow-brutal-lg w-full block overflow-hidden"
                    style={{ aspectRatio: `${VIEW_W} / ${VIEW_H}`, cursor: "crosshair" }}
                >
                    <VoxelScene
                        stateRef={stateRef}
                        aimRef={aimRef}
                        cameraYawRef={cameraYawRef}
                        pickerRef={pickerRef}
                        clickMarkerRef={moveTargetRef}
                        entityPickerRef={entityPickerRef}
                        attackTargetRef={attackTargetRef}
                    />
                </div>
                {/* Crosshair */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                    <div className="w-4 h-4 border-2 border-white rounded-full mix-blend-difference" />
                </div>
                {/* Death overlay — shows what items dropped + where */}
                {you && !you.alive && (
                    <div data-testid="mmo-death-overlay" className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center pointer-events-none px-4">
                        <p className="font-heading font-black text-5xl text-vibrantpink tracking-tighter mb-1">YOU DIED</p>
                        {you.last_killer && <p className="font-body text-white/80 text-sm mb-2">killed by @{you.last_killer}</p>}
                        {((you.last_death_drops && you.last_death_drops.length > 0) || (you.last_death_credits ?? 0) > 0) && (
                            <div className="bg-ink/90 border-[3px] border-vibrantpink rounded-xl px-4 py-3 mt-2 max-w-sm w-full" data-testid="mmo-death-drops">
                                <p className="font-heading font-black text-[10px] uppercase tracking-widest text-vibrantyellow/80 mb-2 text-center">Items dropped on the ground</p>
                                <ul className="space-y-1">
                                    {(you.last_death_credits ?? 0) > 0 && (
                                        <li className="flex items-center justify-between font-heading text-white text-sm">
                                            <span className="flex items-center gap-1.5"><Coins className="w-3.5 h-3.5 text-vibrantyellow" strokeWidth={2.5} /> Coin pile</span>
                                            <span className="font-black tabular-nums">{you.last_death_credits} cr</span>
                                        </li>
                                    )}
                                    {(you.last_death_drops || []).map((d, i) => (
                                        <li key={i} className="flex items-center justify-between font-heading text-white text-sm">
                                            <span className="capitalize">{d.slot}: <span className="font-black">{d.name}</span></span>
                                            <span className="text-vibrantpink text-xs">lost</span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="font-body text-[11px] text-white/70 mt-2 text-center">
                                    at <span className="font-heading font-black text-vibrantyellow tabular-nums">({Math.round(you.last_death_x ?? 0)}, {Math.round(you.last_death_y ?? 0)})</span> — race back!
                                </p>
                            </div>
                        )}
                        <p className="font-heading font-black text-vibrantyellow text-base mt-3">
                            {you.respawn_in > 0 ? `respawn in ${Math.ceil(you.respawn_in)}s` : "press R to respawn"}
                        </p>
                    </div>
                )}
                <Hud you={you} online={state?.online || 0} leaderboard={state?.leaderboard || []} onBank={() => setDepositOpen(true)} onShop={() => setShopOpen(true)} />
                <Minimap you={you} players={state?.players || []} world={state?.world} />
                {!connected && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-2xl">
                        <div className="brutal-card p-5 bg-white text-center">
                            <Loader2 className="w-8 h-8 mx-auto text-vibrantorange animate-spin mb-2" strokeWidth={2.5} />
                            <p className="font-heading font-black text-ink">Connecting to the realm…</p>
                            {error && <p className="font-body text-sm text-vibrantpink mt-1">{error}</p>}
                        </div>
                    </div>
                )}
                {bankedToast > 0 && (
                    <div data-testid="mmo-banked-toast" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 brutal-card p-5 bg-vibrantorange text-white pointer-events-none">
                        <p className="font-heading font-black text-3xl">+{bankedToast} cr banked</p>
                    </div>
                )}
            </div>

            {/* Footer controls reference */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center max-w-3xl mx-auto">
                <div className="brutal-card p-2 bg-white text-xs font-heading font-bold text-ink/70">L-Click <span className="block text-[10px] text-ink/40">attack</span></div>
                <div className="brutal-card p-2 bg-white text-xs font-heading font-bold text-ink/70">R-Click <span className="block text-[10px] text-ink/40">walk here</span></div>
                <div className="brutal-card p-2 bg-white text-xs font-heading font-bold text-ink/70">A / D <span className="block text-[10px] text-ink/40">rotate cam</span></div>
                <div className="brutal-card p-2 bg-white text-xs font-heading font-bold text-ink/70">R / ESC <span className="block text-[10px] text-ink/40">respawn / cancel</span></div>
            </div>

            {/* Deposit modal */}
            {depositOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div data-testid="mmo-deposit-modal" className="brutal-card p-6 bg-white max-w-md w-full">
                        <div className="flex items-center gap-2 mb-3">
                            <Wallet className="w-5 h-5 text-vibrantorange" strokeWidth={2.5} />
                            <h3 className="font-heading font-black text-2xl text-ink tracking-tighter">Bring credits to the field</h3>
                        </div>
                        <p className="font-body text-sm text-ink/60 mb-4">
                            Carry credits are at risk. If you die, 50% drop as loot and 50% go to your killer. Bank in Safe Town to lock them in.
                        </p>
                        <label className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60 block mb-1">
                            Amount (you have {credits})
                        </label>
                        <input
                            type="number"
                            data-testid="mmo-deposit-input"
                            min={1}
                            max={Math.min(credits, 5000)}
                            value={depositAmt}
                            onChange={(e) => setDepositAmt(e.target.value)}
                            className="brutal-input"
                        />
                        <div className="grid grid-cols-4 gap-2 mt-2">
                            {[5, 20, 50, 100].map((v) => (
                                <button key={v} onClick={() => setDepositAmt(v)} className="brutal-btn-white text-xs py-1.5">{v}</button>
                            ))}
                        </div>
                        {depositErr && <p data-testid="mmo-deposit-error" className="mt-2 font-body text-sm text-vibrantpink font-semibold">{depositErr}</p>}
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setDepositOpen(false)} className="brutal-btn-white flex-1">Cancel</button>
                            <button
                                onClick={doDeposit}
                                disabled={depositing || credits <= 0}
                                data-testid="mmo-deposit-confirm"
                                className="brutal-btn-primary flex-1"
                            >
                                {depositing ? "Depositing…" : `Bring ${depositAmt}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <MmoShopModal
                open={shopOpen}
                onClose={() => setShopOpen(false)}
                credits={credits}
                currentTier={you?.tier || "rusty"}
                currentWeapon={you?.weapon || ""}
                currentHat={you?.hat || ""}
                currentNeck={you?.neck || ""}
                combatLevel={you?.combat_level || 1}
                onPurchased={(data) => { setCredits(data.credits); /* keep modal open so they can see "Equipped" */ }}
            />
        </div>
    );
}
